import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import BirthdayPicker from "@/components/BirthdayPicker";

export default function Login() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({});
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next") || "/account";

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, pw); toast.success("Welcome back"); nav(next); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <AuthLayout title={settings.login_title || "Log In"} subtitle={settings.login_subtitle}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
        <Field label="Password" type="password" value={pw} onChange={setPw} testid="login-password" />
        <button className="btn-primary w-full" disabled={busy} data-testid="login-submit">
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
      <div className="text-sm text-zinc-400 mt-6 text-center">
        No account? <Link to="/register" className="text-[var(--cl-orange)]">Create one</Link>
      </div>
    </AuthLayout>
  );
}

export function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [birthday, setBirthday] = useState("");
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({});
  const { register } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(name, email, pw, birthday ? { birthday } : {});
      toast.success("Welcome to Card Lobster");
      nav("/account");
    }
    catch (err) { toast.error(err?.response?.data?.detail || "Registration failed"); }
    finally { setBusy(false); }
  };

  return (
    <AuthLayout title={settings.register_title || "Create Account"} subtitle={settings.register_subtitle}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full Name" value={name} onChange={setName} testid="register-name" />
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="register-email" />
        <Field label="Password" type="password" value={pw} onChange={setPw} testid="register-password" />
        <BirthdayField value={birthday} onChange={setBirthday} />
        <button className="btn-primary w-full" disabled={busy} data-testid="register-submit">
          {busy ? "Creating…" : "Sign Up"}
        </button>
      </form>
      <div className="text-sm text-zinc-400 mt-6 text-center">
        Already registered? <Link to="/login" className="text-[var(--cl-orange)]">Sign in</Link>
      </div>
    </AuthLayout>
  );
}

function BirthdayField({ value, onChange }) {
  return (
    <div>
      <label className="text-label text-zinc-500 flex items-center gap-2">
        <span>Birthday</span>
        <span className="text-[10px] text-zinc-600 normal-case tracking-normal">optional · unlock birthday perks</span>
      </label>
      <div className="mt-2">
        <BirthdayPicker value={value} onChange={onChange} testidPrefix="register-birthday" />
      </div>
    </div>
  );
}

function AuthLayout({ title, subtitle, children }) {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <img src="/brand/card-lobster-mascot.jpg" alt="Card Lobster"
             className="w-20 h-20 rounded-full ring-2 ring-[var(--cl-primary)]/50 object-cover mx-auto" />
        <h1 className="font-display text-5xl text-center mt-6">{title}</h1>
        {subtitle && <p className="text-center text-sm text-zinc-400 mt-3" data-testid="auth-subtitle">{subtitle}</p>}
        <div className="mt-8 border border-white/10 rounded-md bg-[var(--cl-surface)] p-8">
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-label text-zinc-500">{label}</label>
      <input
        type={type}
        value={value}
        required
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full mt-2 bg-black border border-white/10 rounded px-4 py-3 focus:outline-none focus:border-[var(--cl-primary)]"
      />
    </div>
  );
}
